<template>
  <div class="sales-intel">
    <div class="sales-kpi-grid">
      <button type="button" class="sales-kpi" @click="$emit('kpi', 'replace')">
        <div class="label">{{ '기간 총 교체수' | translate }}</div>
        <div class="value">{{ num(kpis.replaceCount) }}</div>
      </button>
      <button type="button" class="sales-kpi" @click="$emit('kpi', 'lowEmpty')">
        <div class="label">{{ 'LOW/EMPTY' | translate }}</div>
        <div class="value">{{ num(kpis.lowEmptyCount) }}</div>
      </button>
      <button type="button" class="sales-kpi warn" @click="$emit('kpi', 'error')">
        <div class="label">{{ '에러 슬롯' | translate }}</div>
        <div class="value">{{ num(kpis.errorCount) }}</div>
      </button>
      <button type="button" class="sales-kpi" @click="$emit('kpi', 'urgent')">
        <div class="label">{{ '긴급' | translate }}</div>
        <div class="value">{{ num(kpis.urgentCount) }}</div>
      </button>
      <div class="sales-kpi">
        <div class="label">{{ '호환 비율' | translate }}</div>
        <div class="value">{{ compatLabel }}</div>
        <div class="meta">
          {{ '정품' | translate }} {{ num(kpis.oemSlotCount) }}
          · {{ '호환' | translate }} {{ num(kpis.compatibleSlotCount) }}
        </div>
      </div>
    </div>

    <div class="sales-search">
      <div class="type-group">
        <button
          v-for="item in searchTypes"
          :key="item.value"
          type="button"
          class="type-btn"
          :class="{ on: searchType === item.value }"
          @click="$emit('update:searchType', item.value)"
        >
          {{ item.label | translate }}
        </button>
      </div>
      <div class="suggest-wrap">
        <input
          :value="keyword"
          type="text"
          class="sales-input"
          :placeholder="searchPlaceholder"
          autocomplete="off"
          @input="onKeywordInput"
          @keydown.enter.prevent="onEnter"
          @keydown.down.prevent="moveSuggest(1)"
          @keydown.up.prevent="moveSuggest(-1)"
          @focus="showSuggest = suggestions.length > 0"
        />
        <ul v-if="showSuggest && suggestions.length" class="suggest-list">
          <li
            v-for="(item, idx) in suggestions"
            :key="item.type + '-' + item.value + '-' + idx"
            :class="{ on: idx === suggestIndex }"
            @mousedown.prevent="$emit('pick', item)"
          >
            <span class="suggest-type">{{ typeLabel(item.type) }}</span>
            <span class="suggest-label">{{ item.label }}</span>
            <span v-if="item.manufacturerName && item.type !== 'MANUFACTURER'" class="suggest-extra">
              {{ item.manufacturerName }}
            </span>
          </li>
        </ul>
      </div>
      <button type="button" class="sales-btn" @click="$emit('search')">
        {{ '검색' | translate }}
      </button>
      <button v-if="hasFilter" type="button" class="sales-btn outline" @click="$emit('clear')">
        {{ '초기화' | translate }}
      </button>
      <button type="button" class="sales-btn outline" @click="$emit('export')">
        {{ 'CSV' | translate }}
      </button>
    </div>

    <div v-if="drill" class="sales-crumb">
      <button type="button" class="crumb-back" @click="$emit('clear-drill')">
        {{ '← 전체' | translate }}
      </button>
      <span>{{ drill.label }}</span>
      <span class="crumb-hint">{{ drillHint }}</span>
    </div>

    <div class="sales-tabs">
      <button
        v-for="tab in groupTabs"
        :key="tab.value"
        type="button"
        :class="{ active: groupBy === tab.value }"
        @click="$emit('change-group', tab.value)"
      >
        {{ tab.label | translate }}
      </button>
    </div>

    <p class="sales-hint">{{ axisHint }}</p>

    <div class="sales-table-wrap" data-supply-drawer-keep>
      <table class="sales-table">
        <thead>
          <tr>
            <th>{{ axisNameHeader }}</th>
            <th v-if="groupBy === 'SUPPLY'">{{ '제조사' | translate }}</th>
            <th v-if="groupBy === 'SUPPLY'">{{ '품번' | translate }}</th>
            <th v-if="groupBy === 'MODEL'">{{ '제조사' | translate }}</th>
            <th>{{ '교체수' | translate }}</th>
            <th>{{ '프린터수' | translate }}</th>
            <th v-if="groupBy !== 'RENTAL'">{{ '임대업체수' | translate }}</th>
            <th v-if="groupBy === 'SUPPLY'">{{ 'LOW/EMPTY' | translate }}</th>
            <th v-if="groupBy === 'SUPPLY'">{{ '에러' | translate }}</th>
            <th v-if="groupBy === 'SUPPLY'">{{ '호환' | translate }}</th>
            <th>{{ '월간예측' | translate }}</th>
            <th>{{ '최근교체' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td :colspan="colspan" class="empty">{{ '데이터가 없습니다.' | translate }}</td>
          </tr>
          <tr
            v-for="(row, idx) in rows"
            :key="row.groupKey || idx"
            class="clickable-row"
            @click="$emit('row-click', row)"
          >
            <td>{{ axisLabel(row) }}</td>
            <td v-if="groupBy === 'SUPPLY'">{{ row.manufacturerName || '-' }}</td>
            <td v-if="groupBy === 'SUPPLY'">{{ row.partNumber || '-' }}</td>
            <td v-if="groupBy === 'MODEL'">{{ row.manufacturerName || '-' }}</td>
            <td>{{ num(row.replaceCount) }}</td>
            <td>{{ num(row.printerCount) }}</td>
            <td v-if="groupBy !== 'RENTAL'">{{ num(row.rentalCompanyCount) }}</td>
            <td v-if="groupBy === 'SUPPLY'">{{ num(row.lowEmptyCount) }}</td>
            <td v-if="groupBy === 'SUPPLY'">{{ num(row.errorCount) }}</td>
            <td v-if="groupBy === 'SUPPLY'">{{ rowCompat(row) }}</td>
            <td>{{ formatNullable(row.monthlyForecast) }}</td>
            <td class="nowrap">{{ row.lastReplacedDatetime || '-' }}</td>
            <td>
              <button
                v-if="groupBy === 'SUPPLY'"
                type="button"
                class="link-btn"
                @click.stop="$emit('sku-insight', row)"
              >
                {{ '호환비교' | translate }}
              </button>
              <button
                v-if="groupBy === 'SUPPLY'"
                type="button"
                class="link-btn"
                @click.stop="$emit('market', row)"
              >
                {{ '구매예상' | translate }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="sales-paging">
      <button type="button" class="sales-btn outline" :disabled="page <= 1" @click="$emit('page-change', page - 1)">
        {{ '이전' | translate }}
      </button>
      <span>{{ page }} / {{ totalPages }}</span>
      <button
        type="button"
        class="sales-btn outline"
        :disabled="page >= totalPages"
        @click="$emit('page-change', page + 1)"
      >
        {{ '다음' | translate }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SupplySalesIntelPanel',
  props: {
    rows: { type: Array, default: () => [] },
    keyword: { type: String, default: '' },
    searchType: { type: String, default: 'AUTO' },
    groupBy: { type: String, default: 'SUPPLY' },
    suggestions: { type: Array, default: () => [] },
    kpis: { type: Object, default: () => ({}) },
    drill: { type: Object, default: null },
    page: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    hasFilter: { type: Boolean, default: false },
  },
  data() {
    return {
      showSuggest: false,
      suggestIndex: -1,
      searchTypes: [
        { value: 'AUTO', label: '자동' },
        { value: 'MANUFACTURER', label: '제조사' },
        { value: 'MODEL', label: '모델' },
        { value: 'PART', label: '품번' },
        { value: 'DESCRIPTION', label: '설명' },
      ],
      groupTabs: [
        { value: 'SUPPLY', label: '소모품별' },
        { value: 'MODEL', label: '프린터 모델별' },
        { value: 'RENTAL', label: '임대업체별' },
      ],
    }
  },
  computed: {
    searchPlaceholder() {
      if (this.searchType === 'MANUFACTURER') return this.t('제조사명')
      if (this.searchType === 'MODEL') return this.t('프린터 모델명')
      if (this.searchType === 'PART') return this.t('품번 / SKU')
      if (this.searchType === 'DESCRIPTION') return this.t('소모품 설명')
      return this.t('제조사 / 모델 / 품번 / 설명')
    },
    axisNameHeader() {
      if (this.groupBy === 'MODEL') return this.t('프린터 모델')
      if (this.groupBy === 'RENTAL') return this.t('임대업체')
      return this.t('구분')
    },
    axisHint() {
      if (this.groupBy === 'MODEL') {
        return this.t('제조사 포트폴리오입니다. 행을 누르면 해당 모델의 소모품별 집계로 이동합니다.')
      }
      if (this.groupBy === 'RENTAL') {
        return this.t(
          '임대업체 랭킹입니다. 거래처명·시리얼은 업체를 고른 뒤에만 봅니다. 행을 누르면 그 업체의 소모품별로 이동합니다.',
        )
      }
      return this.t('소모품(SKU) 집계입니다. 행을 누르면 해당 품번의 임대업체 랭킹으로 들어갑니다.')
    },
    drillHint() {
      if (!this.drill) return ''
      if (this.drill.kind === 'SKU') return this.t('이 품번의 임대업체 랭킹')
      if (this.drill.kind === 'MODEL') return this.t('이 모델의 소모품')
      if (this.drill.kind === 'RENTAL') return this.t('이 업체의 소모품')
      return ''
    },
    colspan() {
      if (this.groupBy === 'SUPPLY') return 12
      if (this.groupBy === 'MODEL') return 7
      return 6
    },
    compatLabel() {
      const ratio = this.kpis.compatRatio
      if (ratio === null || ratio === undefined || ratio === '') return this.t('데이터 부족')
      return `${ratio}%`
    },
  },
  watch: {
    suggestions() {
      this.suggestIndex = -1
      this.showSuggest = this.suggestions.length > 0
    },
  },
  methods: {
    t(text) {
      return this.$options.filters && this.$options.filters.translate
        ? this.$options.filters.translate(text)
        : text
    },
    num(val) {
      return Number(val || 0).toLocaleString()
    },
    formatNullable(val) {
      if (val === null || val === undefined || val === '') return this.t('데이터 부족')
      return val
    },
    typeLabel(type) {
      const map = {
        MANUFACTURER: this.t('제조사'),
        MODEL: this.t('모델'),
        PART: this.t('품번'),
        DESCRIPTION: this.t('설명'),
      }
      return map[type] || type
    },
    axisLabel(row) {
      if (this.groupBy === 'RENTAL') return row.rentalcompanyName || row.groupLabel || '-'
      if (this.groupBy === 'MODEL') return row.printerModelName || row.groupLabel || '-'
      return row.groupLabel || row.supplyLabel || '-'
    },
    rowCompat(row) {
      const total = Number(row.slotCount || 0)
      const compatible = Number(row.compatibleCount || 0)
      if (!total) return '-'
      return `${Math.round((compatible / total) * 100)}%`
    },
    onKeywordInput(e) {
      this.$emit('update:keyword', e.target.value)
      this.$emit('suggest', e.target.value)
    },
    onEnter() {
      if (this.suggestIndex >= 0 && this.suggestions[this.suggestIndex]) {
        this.$emit('pick', this.suggestions[this.suggestIndex])
        this.showSuggest = false
        return
      }
      this.showSuggest = false
      this.$emit('search')
    },
    moveSuggest(dir) {
      if (!this.suggestions.length) return
      const next = this.suggestIndex + dir
      const max = this.suggestions.length - 1
      this.suggestIndex = next < 0 ? max : next > max ? 0 : next
    },
  },
}
</script>

<style scoped>
.sales-intel {
  width: 100%;
}
.sales-kpi-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.sales-kpi {
  min-width: 140px;
  flex: 1;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
  font: inherit;
}
.sales-kpi:hover {
  border-color: #2563eb;
}
.sales-kpi.warn .value {
  color: #b45309;
}
.sales-kpi .label {
  font-size: 12px;
  color: #64748b;
}
.sales-kpi .value {
  margin-top: 4px;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}
.sales-kpi .meta {
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
}
.sales-search {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.type-group {
  display: flex;
  gap: 4px;
}
.type-btn {
  height: 32px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.type-btn.on {
  background: #0f172a;
  border-color: #0f172a;
  color: #fff;
}
.suggest-wrap {
  position: relative;
  flex: 1;
  min-width: 260px;
}
.sales-input {
  width: 100%;
  height: 34px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 10px;
  box-sizing: border-box;
}
.suggest-list {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: 36px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  max-height: 240px;
  overflow: auto;
}
.suggest-list li {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 13px;
}
.suggest-list li.on,
.suggest-list li:hover {
  background: #eff6ff;
}
.suggest-type {
  font-size: 11px;
  color: #64748b;
  min-width: 40px;
}
.suggest-label {
  font-weight: 600;
  color: #0f172a;
}
.suggest-extra {
  margin-left: auto;
  font-size: 12px;
  color: #64748b;
}
.sales-btn {
  height: 34px;
  padding: 0 12px;
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.sales-btn.outline {
  background: #fff;
  color: #2563eb;
}
.sales-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.sales-crumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
}
.crumb-back {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 4px;
  height: 28px;
  padding: 0 8px;
  cursor: pointer;
}
.crumb-hint {
  color: #64748b;
}
.sales-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}
.sales-tabs button {
  border: 1px solid #cbd5e1;
  background: #fff;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}
.sales-tabs button.active {
  background: #0f172a;
  border-color: #0f172a;
  color: #fff;
}
.sales-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.sales-table-wrap {
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.sales-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sales-table th,
.sales-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}
.sales-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover {
  background: #f8fafc;
}
.empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px 8px;
}
.nowrap {
  white-space: nowrap;
}
.link-btn {
  border: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  font-size: 12px;
  padding: 0 4px;
}
.sales-paging {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
</style>
